const STORED_AUTH_KEY = 'ua_vlt_90d_v1';
const CIPHER_PREFIX = 'gcmv1';

type StoredBlob = {
  a: string;
  b: string;
  f: string;
  t: string;
};

type VaultPair = {
  id: string;
  token: string;
};

const getBrowserCrypto = (): Crypto | undefined =>
  window.crypto || (window as Window & { msCrypto?: Crypto }).msCrypto;

const bytesToBase64 = (bytes: Uint8Array): string => {
  let binary = '';

  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return window.btoa(binary);
};

const base64ToArrayBuffer = (base64: string): ArrayBuffer => {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }

  return bytes.buffer as ArrayBuffer;
};

const getAesKey = async (
  subtle: SubtleCrypto,
  encoder: TextEncoder,
): Promise<CryptoKey> => {
  const seed = `${window.location.hostname}|remembered-login-v1`;
  const digest = await subtle.digest('SHA-256', encoder.encode(seed));

  return subtle.importKey('raw', digest, { name: 'AES-GCM' }, false, [
    'encrypt',
    'decrypt',
  ]);
};

const encryptValue = async (value: string): Promise<string | null> => {
  const browserCrypto = getBrowserCrypto();
  const subtle = browserCrypto?.subtle;

  if (!browserCrypto || !subtle || typeof TextEncoder === 'undefined') {
    return null;
  }

  try {
    const encoder = new TextEncoder();
    const ivBytes = browserCrypto.getRandomValues(new Uint8Array(12));
    const key = await getAesKey(subtle, encoder);
    const encrypted = await subtle.encrypt(
      { name: 'AES-GCM', iv: ivBytes },
      key,
      encoder.encode(value),
    );

    return `${CIPHER_PREFIX}:${bytesToBase64(ivBytes)}:${bytesToBase64(
      new Uint8Array(encrypted),
    )}`;
  } catch {
    return null;
  }
};

const decryptValue = async (value: string): Promise<string | null> => {
  if (!value || !value.startsWith(`${CIPHER_PREFIX}:`)) {
    return null;
  }

  const browserCrypto = getBrowserCrypto();
  const subtle = browserCrypto?.subtle;

  if (
    !subtle ||
    typeof TextEncoder === 'undefined' ||
    typeof TextDecoder === 'undefined'
  ) {
    return null;
  }

  const [, ivBase64, encryptedBase64] = value.split(':');

  if (!ivBase64 || !encryptedBase64) {
    return null;
  }

  try {
    const key = await getAesKey(subtle, new TextEncoder());
    const decrypted = await subtle.decrypt(
      { name: 'AES-GCM', iv: base64ToArrayBuffer(ivBase64) },
      key,
      base64ToArrayBuffer(encryptedBase64),
    );

    return new TextDecoder().decode(decrypted);
  } catch {
    return null;
  }
};

export const clearRememberedLogin = (): void => {
  localStorage.removeItem(STORED_AUTH_KEY);
};

export const saveRememberedLogin = async ({
  id,
  token,
  days = 90,
}: VaultPair & { days?: number }): Promise<void> => {
  const encryptedId = await encryptValue(id);
  const encryptedToken = await encryptValue(token);

  if (!encryptedId || !encryptedToken) {
    clearRememberedLogin();
    return;
  }

  const fromDate = new Date();
  const toDate = new Date(fromDate.getTime() + days * 24 * 60 * 60 * 1000);

  const payload: StoredBlob = {
    a: encryptedId,
    b: encryptedToken,
    f: fromDate.toISOString(),
    t: toDate.toISOString(),
  };

  localStorage.setItem(STORED_AUTH_KEY, JSON.stringify(payload));
};

export const getRememberedLoginIfValid =
  async (): Promise<VaultPair | null> => {
    const raw = localStorage.getItem(STORED_AUTH_KEY);

    if (!raw) {
      return null;
    }

    try {
      const parsed: unknown = JSON.parse(raw);

      if (parsed === null || typeof parsed !== 'object') {
        clearRememberedLogin();
        return null;
      }

      const stored = parsed as Record<string, unknown>;

      if (
        typeof stored.a !== 'string' ||
        typeof stored.b !== 'string' ||
        typeof stored.f !== 'string' ||
        typeof stored.t !== 'string'
      ) {
        clearRememberedLogin();
        return null;
      }

      const toDateTime = new Date(stored.t).getTime();

      if (!toDateTime || toDateTime <= Date.now()) {
        clearRememberedLogin();
        return null;
      }

      const id = await decryptValue(stored.a);
      const token = await decryptValue(stored.b);

      if (!id || !token) {
        clearRememberedLogin();
        return null;
      }

      return { id, token };
    } catch {
      clearRememberedLogin();
      return null;
    }
  };
